import React, { useState, useEffect } from 'react';
import { Plus, Sparkles } from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useSettings } from './contexts/SettingsContext';
import apiService from './apiService';
import { renderFieldError } from './utils/formValidation';

const AddPatientView = ({ setCurrentView, addPatient }) => {
    const { showAlert } = useAlert();
    const { appSettings } = useSettings();

    const [newPatient, setNewPatient] = useState({
        name: '', age: '', phone: '', department: '', symptoms: '', medicalHistory: '',
        email: '', address: '', gender: '', emergencyContact: '',
    });
    const [llmRecommendation, setLlmRecommendation] = useState('');
    const [isGeneratingRecommendation, setIsGeneratingRecommendation] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [checkInImmediately, setCheckInImmediately] = useState(true); // Default to true
    const [visitType, setVisitType] = useState('consultation');
    const [priority, setPriority] = useState('medium');
    
    // Department dropdown states
    const [departments, setDepartments] = useState([]);

    // Fetch departments on mount
    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            const depts = await apiService.getDepartments();
            setDepartments(depts);
        } catch (error) {
            console.error('Failed to fetch departments:', error);
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!newPatient.name || newPatient.name.trim() === '') {
            errors.name = 'Patient name is required';
        }
        
        if (!newPatient.age || newPatient.age === '') {
            errors.age = 'Age is required';
        } else if (parseInt(newPatient.age) < 0 || parseInt(newPatient.age) > 150) {
            errors.age = 'Please enter a valid age (0-150)';
        }
        
        if (!newPatient.phone || newPatient.phone.trim() === '') {
            errors.phone = 'Phone number is required';
        }
        
        if (!newPatient.department || newPatient.department === '') {
            errors.department = 'Department is required';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleAddPatient = async () => {
        setAttemptedSubmit(true);
        
        if (!validateForm()) {
            showAlert('Please fill in all required fields marked in red', 'error');
            return;
        }
        
        try {
            const patientOrgId = appSettings.id;
            
            // Step 1: Create patient
            const createdPatient = await addPatient({
                ...newPatient,
                age: parseInt(newPatient.age),
                organizationId: patientOrgId,
            });
            
            console.log('✅ Patient created:', createdPatient);
            
            // Step 2: Check in immediately if option selected
            if (checkInImmediately && createdPatient) {
                try {
                    console.log('🔄 Checking in patient immediately...');
                    
                    // Assign queue number and check in
                    const queueResult = await apiService.assignQueueNumber(createdPatient.id);
                    console.log('✅ Queue assigned:', queueResult);
                    
                    // Create appointment if not walk-in
                    if (visitType !== 'walk-in') {
                        await apiService.createAppointment({
                            patientId: createdPatient.id,
                            department: newPatient.department,
                            appointmentType: visitType,
                            priority: priority,
                            notes: `New patient registration. ${newPatient.symptoms || ''}`,
                            appointmentTime: new Date().toISOString()
                        });
                    }
                    
                    showAlert(
                        `✅ Patient registered & checked in! Queue #${queueResult.assignQueueNumber.queueNumber}`, 
                        'success'
                    );
                } catch (checkInError) {
                    console.error('Error during check-in:', checkInError);
                    showAlert(
                        `⚠️ Patient registered successfully but check-in failed. Please check in manually.`, 
                        'warning'
                    );
                }
            } else {
                showAlert('✅ Patient registered successfully!', 'success');
            }
            
            // Reset form
            setNewPatient({
                name: '', age: '', phone: '', department: '', symptoms: '', medicalHistory: '',
                email: '', address: '', gender: '', emergencyContact: '',
            });
            setLlmRecommendation('');
            setValidationErrors({});
            setAttemptedSubmit(false);
            setCurrentView('patients');
        } catch (error) {
            console.error('Failed to add patient:', error);
            showAlert(`Failed to add patient: ${error.message}`, 'error');
        }
    };

    const analyzeSymptoms = async () => {
        if (!newPatient.symptoms) {
            showAlert("Please enter symptoms to analyze.", 'info');
            setLlmRecommendation('');
            return;
        }

        setIsGeneratingRecommendation(true);
        setLlmRecommendation('');

        try {
            const text = await apiService.analyzeSymptomsLLM(newPatient.symptoms);
            setLlmRecommendation(text);
            showAlert('Symptoms analyzed successfully!', 'success');
        } catch (error) {
            setLlmRecommendation('Error analyzing symptoms. Please check your network connection or try again later.');
            showAlert('Error analyzing symptoms.', 'error');
        } finally {
            setIsGeneratingRecommendation(false);
        }
    };

    return (
        <div className="space-y-6 bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-gray-800">Add New Patient</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleAddPatient(); }} className="space-y-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Patient Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        value={newPatient.name}
                        onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })}
                        className={`mt-1 block w-full border rounded-md shadow-sm p-2 focus:outline-none ${
                            attemptedSubmit && validationErrors.name
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                    />
                    {renderFieldError(attemptedSubmit, 'name', validationErrors)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="age" className="block text-sm font-medium text-gray-700">
                            Age <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            id="age"
                            value={newPatient.age}
                            onChange={(e) => setNewPatient({ ...newPatient, age: e.target.value })}
                            className={`mt-1 block w-full border rounded-md shadow-sm p-2 focus:outline-none ${
                                attemptedSubmit && validationErrors.age
                                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                        />
                        {renderFieldError(attemptedSubmit, 'age', validationErrors)}
                    </div>
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                            Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            value={newPatient.phone}
                            onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
                            className={`mt-1 block w-full border rounded-md shadow-sm p-2 focus:outline-none ${
                                attemptedSubmit && validationErrors.phone
                                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                        />
                        {renderFieldError(attemptedSubmit, 'phone', validationErrors)}
                    </div>
                </div>
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email (Optional)</label>
                    <input
                        type="email"
                        id="email"
                        value={newPatient.email}
                        onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address (Optional)</label>
                    <input
                        type="text"
                        id="address"
                        value={newPatient.address}
                        onChange={(e) => setNewPatient({ ...newPatient, address: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-700">Gender (Optional)</label>
                    <select
                        id="gender"
                        value={newPatient.gender}
                        onChange={(e) => setNewPatient({ ...newPatient, gender: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="emergencyContact" className="block text-sm font-medium text-gray-700">Emergency Contact (Optional)</label>
                    <input
                        type="tel"
                        id="emergencyContact"
                        value={newPatient.emergencyContact}
                        onChange={(e) => setNewPatient({ ...newPatient, emergencyContact: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div>
                    <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                        Department <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="department"
                        list="departments-datalist"
                        value={newPatient.department}
                        onChange={(e) => setNewPatient({ ...newPatient, department: e.target.value })}
                        placeholder="Type or select department..."
                        className={`mt-1 block w-full border rounded-md shadow-sm p-2 focus:outline-none ${
                            attemptedSubmit && validationErrors.department
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                    />
                    <datalist id="departments-datalist">
                        {departments.map((dept) => (
                            <option key={dept.id} value={dept.name} />
                        ))}
                    </datalist>
                    {renderFieldError(attemptedSubmit, 'department', validationErrors)}
                    <p className="mt-1 text-xs text-gray-500">
                        {departments.length > 0 
                            ? 'Start typing to see suggestions from existing departments'
                            : 'Type department name (e.g., Cardiology, Pediatrics, Emergency)'
                        }
                    </p>
                </div>
                <div>
                    <label htmlFor="symptoms" className="block text-sm font-medium text-gray-700">Symptoms (for LLM analysis)</label>
                    <textarea
                        id="symptoms"
                        rows="3"
                        value={newPatient.symptoms}
                        onChange={(e) => {
                            setNewPatient({ ...newPatient, symptoms: e.target.value });
                            setLlmRecommendation('');
                        }}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., severe chest pain, shortness of breath, radiating arm pain"
                    ></textarea>
                    <button
                        type="button"
                        onClick={analyzeSymptoms}
                        disabled={isGeneratingRecommendation || !newPatient.symptoms}
                        className={`mt-2 bg-purple-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-600 transition-colors ${
                            isGeneratingRecommendation || !newPatient.symptoms ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                        {isGeneratingRecommendation ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Analyzing...
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4" /> Analyze Symptoms
                            </>
                        )}
                    </button>
                    {llmRecommendation && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-md text-blue-800">
                            <strong>LLM Recommendation:</strong> {llmRecommendation}
                        </div>
                    )}
                </div>
                {appSettings.enableDetailedMedicalHistory && (
                    <div>
                        <label htmlFor="medicalHistory" className="block text-sm font-medium text-gray-700">Medical History</label>
                        <textarea
                            id="medicalHistory"
                            rows="3"
                            value={newPatient.medicalHistory}
                            onChange={(e) => setNewPatient({ ...newPatient, medicalHistory: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Diabetic, Hypertensive, Allergies to Penicillin"
                        ></textarea>
                    </div>
                )}
                
                {/* Check-In Options */}
                <div className="border-t pt-4 mt-4">
                    <div className="flex items-center gap-2 mb-4">
                        <input
                            type="checkbox"
                            id="checkInImmediately"
                            checked={checkInImmediately}
                            onChange={(e) => setCheckInImmediately(e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="checkInImmediately" className="text-sm font-semibold text-gray-900">
                            ✅ Check in patient immediately (Recommended)
                        </label>
                    </div>
                    
                    {checkInImmediately && (
                        <div className="ml-6 space-y-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800 font-medium">Check-In Details</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Visit Type</label>
                                    <select
                                        value={visitType}
                                        onChange={(e) => setVisitType(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="consultation">Consultation</option>
                                        <option value="follow-up">Follow-up</option>
                                        <option value="emergency">Emergency</option>
                                        <option value="walk-in">Walk-in</option>
                                        <option value="procedure">Procedure</option>
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                    <select
                                        value={priority}
                                        onChange={(e) => setPriority(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                            </div>
                            
                            <p className="text-xs text-blue-600">
                                ℹ️ Patient will be assigned a queue number and added to the triage queue automatically.
                            </p>
                        </div>
                    )}
                </div>
                
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            setNewPatient({
                                name: '', age: '', phone: '', department: '', symptoms: '', medicalHistory: '',
                                email: '', address: '', gender: '', emergencyContact: '',
                            });
                            setLlmRecommendation('');
                            setCurrentView('patients');
                        }}
                        className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
                    >
                        <Plus className="w-4 h-4" />
                        {checkInImmediately ? 'Register & Check In' : 'Add Patient'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddPatientView;